import { useCallback, useEffect, useState } from "react";
import { Outlet, NavLink, useLocation, useNavigate, Link } from "react-router-dom";
import {
  Boxes, Database, Home, Settings, Users, Activity,
  Search, ChevronDown, ChevronsUpDown, LogOut, Plus, Check,
  User as UserIcon, Building2, ImageIcon, Gift, Wrench, Layout,
  Link as LinkIcon, Sparkles, Tag as TagIcon, FolderTree,
  ArrowLeftRight, Type, Compass, ListChecks, LayoutGrid,
  CheckSquare, FolderKanban, UserCheck, Receipt, Landmark,
} from "lucide-react";

// Module icon (registry `icon` string) → lucide component.
const MODULE_ICONS = {
  "check-square": CheckSquare,
  "users": Users,
  "folder-kanban": FolderKanban,
  "user-check": UserCheck,
  "receipt": Receipt,
  "landmark": Landmark,
};
import { setActiveCurrency } from "@/lib/currency";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/lib/auth";
import { useAppConfig } from "@/lib/appConfig";
import { useTerminology } from "@/lib/terminology";
import { api } from "@/lib/api";
import { extractErrorMessage } from "@/lib/errors";
import { toast } from "sonner";
import { CommandPalette, useCommandPalette } from "@/components/CommandPalette";
import { NotificationsBell } from "@/components/NotificationsBell";
import { InstallAppMenuItem } from "@/components/InstallAppMenuItem";
import { TabBar } from "@/components/TabBar";
import { useTabs } from "@/lib/tabs";
import { useHotkeys } from "@/hooks/useHotkeys";
import { Keyboard, Printer, BookOpen } from "lucide-react";

// Sidebar nav groups — labels are resolved live via t() inside the render.
// Each item declares its stable `key` (used for testid + t() lookup) alongside
// its `to` path and default label (fallback if t() misses).
// Top group (always first). The apps/data sections are rendered dynamically
// between this and the groups below, so the sidebar reads module-first.
// Everyday nav = Home + your enabled modules. Everything else (set-up, admin)
// lives in the collapsed "Manage" / "Settings" groups at the bottom.
const NAV_HOME = { key: "nav.dashboard", to: "/dashboard", fallback: "Home", icon: Home, testid: "nav-dashboard" };

const MANAGE_ITEMS = [
  { key: "nav.modules",        to: "/modules",   fallback: "Add modules",   icon: LayoutGrid, testid: "nav-modules" },
  { key: "nav.templates",      to: "/templates", fallback: "Starter packs", icon: Gift,       testid: "nav-starter-packs" },
  // Catalog-era tools (media library, print labels, reusable-field library) —
  // only relevant to the single-business catalog build, hidden in the
  // workflow product.
  { key: "nav.media",          to: "/media",     fallback: "Files",         icon: ImageIcon,  testid: "nav-files", singleOnly: true },
  { key: "nav.lib_fields",     to: "/library/fields",     fallback: "Fields library",     icon: ListChecks, testid: "nav-library-fields",     singleOnly: true },
  { key: "nav.lib_categories", to: "/library/categories", fallback: "Categories library", icon: FolderTree, testid: "nav-library-categories", singleOnly: true },
  { key: "nav.lib_tags",       to: "/library/tags",       fallback: "Tags library",       icon: TagIcon,    testid: "nav-library-tags",       singleOnly: true },
  { key: "settings.labels",    to: "/settings/label-presets", fallback: "Label presets",  icon: Printer,    testid: "nav-label-presets", singleOnly: true },
];

const SETTINGS_ITEMS = [
  { key: "settings.org",         to: "/settings/organization", fallback: "Organization", icon: Building2, testid: "nav-organization" },
  { key: "settings.members",     to: "/settings/members",      fallback: "Team & Roles", icon: Users,     testid: "nav-team" },
  { key: "settings.terminology", to: "/settings/terminology",  fallback: "Terminology",  icon: Type,      testid: "nav-terminology" },
  { key: "settings.audit",       to: "/settings/audit-log",    fallback: "Activity",     icon: Activity,  testid: "nav-activity" },
  { key: "settings.profile",     to: "/settings/profile",      fallback: "Profile",      icon: UserIcon,  testid: "nav-profile" },
];

// Nav items hidden in single-business (furnishing) mode — universal-product
// scaffolding that a single business doesn't need.
const SINGLE_BUSINESS_HIDDEN_NAV = new Set(["nav.templates", "settings.terminology"]);

export default function AppLayout() {
  const { user, orgs, activeOrgId, activeRole, logout, switchOrg, refreshMe } = useAuth();
  const { singleBusiness, defaultCurrency, appVersion } = useAppConfig();
  const activeOrg = (orgs || []).find((o) => o.id === activeOrgId) || (orgs || [])[0];
  // Catalog tools show in the single-business build OR a "product" workspace.
  const catalogMode = singleBusiness || activeOrg?.mode === "product";
  // Keep money formatting in sync with the active workspace's currency.
  useEffect(() => {
    setActiveCurrency(activeOrg?.settings?.currency || defaultCurrency || "USD");
  }, [activeOrg?.settings?.currency, defaultCurrency]);
  const { t } = useTerminology();
  const location = useLocation();
  const nav = useNavigate();
  const [seeding, setSeeding] = useState(false);
  const [creatingOrg, setCreatingOrg] = useState(false);
  const [newOrgName, setNewOrgName] = useState("");
  const [orgMenuOpen, setOrgMenuOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useCommandPalette();
  const [collections, setCollections] = useState([]);
  const [modules, setModules] = useState([]);
  // Sidebar sections are COLLAPSED by default (accordion). We persist the set
  // of sections the user has explicitly OPENED; the active module auto-opens.
  const [openSet, setOpenSet] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("ubos.sidebar.open") || "[]")); }
    catch { return new Set(); }
  });
  const toggleSection = (key) => setOpenSet((prev) => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    try { localStorage.setItem("ubos.sidebar.open", JSON.stringify([...next])); } catch { /* ignore */ }
    return next;
  });
  const isOpen = (id, auto = false) => auto || openSet.has(id);

  // ── Multi-tab hotkeys (Phase 8) ──
  const {
    tabs, activeIndex, openTab, closeTab, activateTab, reopenLastClosed,
    nextTab, prevTab, jumpTo,
  } = useTabs();
  useHotkeys("mod+t", () => openTab("/dashboard", { switchTo: true }));
  useHotkeys("mod+w", () => {
    const active = tabs[activeIndex];
    if (active && tabs.length > 1) closeTab(active.id);
  }, [tabs, activeIndex]);
  useHotkeys("mod+shift+t", () => reopenLastClosed());
  useHotkeys("mod+tab",       () => nextTab(), [nextTab]);
  useHotkeys("mod+shift+tab", () => prevTab(), [prevTab]);
  // Excel-style tab switching: Ctrl+Alt+Up = previous, Ctrl+Alt+Down = next.
  useHotkeys("ctrl+alt+arrowup",   () => prevTab(), [prevTab]);
  useHotkeys("ctrl+alt+arrowdown", () => nextTab(), [nextTab]);
  useHotkeys("mod+1", () => jumpTo(1));
  useHotkeys("mod+2", () => jumpTo(2));
  useHotkeys("mod+3", () => jumpTo(3));
  useHotkeys("mod+4", () => jumpTo(4));
  useHotkeys("mod+5", () => jumpTo(5));
  useHotkeys("mod+6", () => jumpTo(6));
  useHotkeys("mod+7", () => jumpTo(7));
  useHotkeys("mod+8", () => jumpTo(8));
  useHotkeys("mod+9", () => jumpTo(9));

  // Load collections for the "My Data" sub-tree. Refetches whenever the
  // active org changes; refresh on window focus so freshly-created
  // collections appear without a full reload.
  const loadCollections = useCallback(async () => {
    if (!activeOrgId) return;
    try {
      const [etRes, modRes] = await Promise.all([
        api.get("/entity-types"),
        api.get("/modules").catch(() => ({ data: [] })),
      ]);
      const list = Array.isArray(etRes.data) ? etRes.data : (etRes.data?.items || []);
      setCollections(list);
      setModules(Array.isArray(modRes.data) ? modRes.data : []);
    } catch {
      setCollections([]);
    }
  }, [activeOrgId]);

  // Refetch on mount, on org change, and on every navigation so the sidebar's
  // collection list never goes stale (e.g. after a rename on another page).
  useEffect(() => { loadCollections(); }, [loadCollections, location.pathname]);
  useEffect(() => {
    const refetch = () => loadCollections();
    window.addEventListener("focus", refetch);
    // Pages broadcast this after creating/deleting a collection (same-route
    // mutations that no navigation would otherwise pick up).
    window.addEventListener("ubos:collections-changed", refetch);
    return () => {
      window.removeEventListener("focus", refetch);
      window.removeEventListener("ubos:collections-changed", refetch);
    };
  }, [loadCollections]);

  const runSeed = async () => {
    setSeeding(true);
    try {
      const r = await api.post("/dev/seed-demo");
      toast.success(`Sample data loaded · ${r.data.created_records} demo items added`);
      window.location.assign("/entity-types");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setSeeding(false);
    }
  };

  const doSwitchOrg = async (org_id) => {
    if (org_id === activeOrgId) {
      setOrgMenuOpen(false);
      return;
    }
    try {
      await switchOrg(org_id);
      setOrgMenuOpen(false);
      toast.success("Workspace switched");
      window.location.assign("/entity-types");
    } catch (e) {
      toast.error(extractErrorMessage(e));
    }
  };

  const doCreateOrg = async (e) => {
    e.preventDefault();
    if (!newOrgName.trim()) return;
    setCreatingOrg(true);
    try {
      const r = await api.post("/orgs", { name: newOrgName.trim() });
      toast.success(`Created ${r.data.org.name}`);
      // apply new tokens (contains org_id) — reload to hydrate everywhere
      const { tokenStore } = await import("@/lib/api");
      tokenStore.set(r.data);
      await refreshMe();
      setNewOrgName("");
      setOrgMenuOpen(false);
      window.location.assign("/entity-types");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setCreatingOrg(false);
    }
  };

  // ── Sidebar helpers + module-centric grouping ──
  const rowBase = "flex items-center gap-2.5 px-3 py-1.5 rounded-md text-sm transition-colors";
  const isVisible = (item) => {
    if (singleBusiness && SINGLE_BUSINESS_HIDDEN_NAV.has(item.key)) return false;
    if (item.singleOnly && !catalogMode) return false;  // catalog tools → product/catalog mode only
    return true;
  };
  const NavRow = ({ item }) => {
    const Icon = item.icon;
    const translated = t(item.key);
    const label = (translated && translated !== item.key) ? translated : item.fallback;
    const active = location.pathname.startsWith(item.to);
    const dtid = item.testid || `nav-${item.to.replace(/\//g, "-").replace(/^-+/, "")}`;
    return (
      <NavLink to={item.to} data-testid={dtid}
        className={active ? `${rowBase} bg-primary/10 text-primary font-medium` : `${rowBase} text-foreground/80 hover:bg-muted`}>
        <Icon className="w-4 h-4" strokeWidth={2} /><span>{label}</span>
      </NavLink>
    );
  };
  const StaticGroup = ({ group }) => {
    const items = group.items.filter(isVisible);
    if (!items.length) return null;
    return (
      <div>
        <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground px-3 mb-1.5">{group.label}</div>
        <div className="space-y-0.5">{items.map((it) => <NavRow key={it.to} item={it} />)}</div>
      </div>
    );
  };
  const CollectionLink = ({ c }) => {
    const subActive = location.pathname === `/entity-types/${c.id}/records`;
    return (
      <NavLink to={`/entity-types/${c.id}/records`} data-testid={`sidebar-collection-${c.key}`}
        className={subActive ? `${rowBase} py-1 text-primary font-medium bg-primary/5` : `${rowBase} py-1 text-foreground/70 hover:bg-muted`}>
        <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: c.color || "#0d9488" }} />
        <span className="truncate text-[13px]">{c.name_plural || c.name_singular || c.key}</span>
      </NavLink>
    );
  };
  // A collapsible sidebar section. `open` is controlled by the caller.
  const Section = ({ id, label, Icon, headerTo, testid, children, action, open }) => {
    const HeaderInner = (
      <>{Icon && <Icon className="w-3.5 h-3.5 shrink-0" />}<span className="truncate">{label}</span></>
    );
    return (
      <div data-testid={testid}>
        <div className="flex items-center gap-1 px-3 mb-1">
          {headerTo ? (
            <NavLink to={headerTo} className="flex items-center gap-1.5 flex-1 min-w-0 text-[10px] font-mono uppercase tracking-wider text-muted-foreground hover:text-foreground">
              {HeaderInner}
            </NavLink>
          ) : (
            <button type="button" onClick={() => toggleSection(id)}
              className="flex items-center gap-1.5 flex-1 min-w-0 text-[10px] font-mono uppercase tracking-wider text-muted-foreground hover:text-foreground text-left">
              {HeaderInner}
            </button>
          )}
          <button type="button" onClick={() => toggleSection(id)}
            className="p-0.5 rounded text-muted-foreground hover:bg-muted hover:text-foreground shrink-0"
            aria-label={open ? "Collapse" : "Expand"} aria-expanded={!!open} data-testid={`toggle-${id}`}>
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "" : "-rotate-90"}`} />
          </button>
        </div>
        {open && <div className="space-y-0.5">{children}{action}</div>}
      </div>
    );
  };

  // A collapsed-by-default group of static nav rows (Manage / Settings).
  const CollapsibleGroup = ({ id, label, items }) => {
    const visible = items.filter(isVisible);
    if (!visible.length) return null;
    return (
      <Section id={id} label={label} testid={`nav-group-${id}`} open={isOpen(id)}>
        {visible.map((it) => <NavRow key={it.to} item={it} />)}
      </Section>
    );
  };

  const enabledModules = modules.filter((m) => m.state === "active");
  const enabledKeys = new Set(enabledModules.map((m) => m.key));
  const byModule = {};
  collections.forEach((c) => {
    const mk = c.module_key && enabledKeys.has(c.module_key) ? c.module_key : "__data__";
    (byModule[mk] = byModule[mk] || []).push(c);
  });
  const ungrouped = byModule["__data__"] || [];
  // Which module owns the collection currently being viewed → auto-expand it.
  const activeEtId = location.pathname.match(/\/entity-types\/([^/]+)/)?.[1];
  const activeModuleKey = collections.find((c) => c.id === activeEtId)?.module_key || null;

  return (
    <div className="h-screen overflow-hidden flex bg-background" data-testid="app-shell">
      {/* ────── Sidebar ────── */}
      <aside
        className="w-[240px] shrink-0 border-r border-border bg-white flex flex-col"
        data-testid="app-sidebar"
      >
        <div className="px-5 pt-6 pb-5">
          <Link to="/entity-types" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
              <Database className="w-4 h-4" strokeWidth={2.25} />
            </div>
            <div className="leading-tight">
              <div className="text-sm font-semibold tracking-tight">UBOS</div>
              <div className="text-[11px] text-muted-foreground">
                Business OS
              </div>
            </div>
          </Link>
        </div>

        <Separator />

        <nav className="flex-1 overflow-y-auto p-3 space-y-4">
          {/* Home */}
          <NavRow item={NAV_HOME} />

          {/* Your enabled modules — collapsed accordion; active auto-expands.
              Nested/child collections (phases, line items…) are hidden here —
              they're reached inside their parent record. */}
          {enabledModules.map((m) => {
            const nested = new Set(m.nested_entities || []);
            const cols = (byModule[m.key] || []).filter((c) => !nested.has(c.key));
            if (cols.length === 0) return null;
            const MIcon = MODULE_ICONS[m.icon] || Boxes;
            const id = `mod:${m.key}`;
            // Clicking the module name opens its primary entity (e.g. CRM → its
            // pipeline, Projects → cases). Falls back to the first collection.
            const home = collections.find((c) => c.key === m.home_entity_type) || cols[0];
            return (
              <Section key={m.key} id={id} label={m.name} Icon={MIcon} testid={`nav-module-${m.key}`}
                headerTo={home ? `/entity-types/${home.id}/records` : undefined}
                open={isOpen(id, m.key === activeModuleKey)}>
                {cols.map((c) => <CollectionLink key={c.id} c={c} />)}
              </Section>
            );
          })}

          {/* Custom collections that don't belong to a module (hidden if none) */}
          {ungrouped.length > 0 && (
            <Section id="data:other" label={t("nav.data") || "Collections"} Icon={Boxes}
              headerTo="/entity-types" testid="nav-other-collections" open={isOpen("data:other")}>
              {ungrouped.map((c) => <CollectionLink key={c.id} c={c} />)}
            </Section>
          )}

          {/* Always-available: create a custom collection */}
          <NavLink to="/entity-types?new=1" data-testid="sidebar-add-collection"
            className={`${rowBase} py-1.5 text-[13px] text-muted-foreground hover:text-primary hover:bg-muted`}>
            <Plus className="w-3.5 h-3.5" /><span>{t("collection.new") || "New collection"}</span>
          </NavLink>

          {/* Set-up + admin — tucked away, collapsed by default */}
          <div className="pt-2">
            <CollapsibleGroup id="grp:manage" label="Manage" items={MANAGE_ITEMS} />
          </div>
          <CollapsibleGroup id="grp:settings" label="Settings" items={SETTINGS_ITEMS} />
        </nav>

        <div className="p-3 border-t border-border">
          {/* "Load sample data" seeds the demo_basic catalog (Products +
              Machines) — a product/catalog affordance. Hide it in service
              (workflow) workspaces where it would dump irrelevant collections. */}
          {!singleBusiness && catalogMode && (
            <Button
              variant="outline"
              className="w-full justify-start gap-2"
              onClick={runSeed}
              disabled={seeding || !activeOrgId}
              data-testid="seed-demo-btn"
            >
              <Sparkles className="w-4 h-4" />
              {seeding ? "Loading…" : "Load sample data"}
            </Button>
          )}
          <div className="mt-3 flex items-center justify-between text-[11px] text-muted-foreground font-mono px-1">
            <span className="truncate" title={activeOrg?.name}>
              {activeOrg?.name || ""}
            </span>
            <span className="text-primary">{activeRole || ""}</span>
          </div>
          <div className="mt-2 text-center text-[10px] text-muted-foreground/70 tracking-wide">
            Powered by <span className="font-semibold text-muted-foreground">Aariko</span>
            {appVersion && <span className="font-mono" data-testid="sidebar-app-version"> · v{appVersion}</span>}
          </div>
        </div>
      </aside>

      {/* ────── Main column ────── */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Topbar */}
        <div
          className="h-14 border-b border-border bg-white flex items-center gap-3 px-6 shrink-0"
          data-testid="app-topbar"
        >
          {/* Org switcher — a plain label in single-business mode (one workspace) */}
          {singleBusiness ? (
            <div className="h-9 flex items-center gap-2 -ml-2 pl-2 pr-2 text-sm max-w-[280px]" data-testid="org-label">
              <div className="w-6 h-6 rounded bg-primary/15 text-primary text-[11px] font-semibold flex items-center justify-center shrink-0">
                {activeOrg?.name?.slice(0, 1) || "—"}
              </div>
              <span className="truncate font-medium">{activeOrg?.name || "Workspace"}</span>
            </div>
          ) : (
          <DropdownMenu open={orgMenuOpen} onOpenChange={setOrgMenuOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="h-9 gap-2 -ml-2 pl-2 pr-2 text-sm max-w-[280px]"
                data-testid="org-switcher"
              >
                <div className="w-6 h-6 rounded bg-primary/15 text-primary text-[11px] font-semibold flex items-center justify-center shrink-0">
                  {activeOrg?.name?.slice(0, 1) || "—"}
                </div>
                <span className="truncate font-medium">
                  {activeOrg?.name || "No workspace"}
                </span>
                <ChevronsUpDown className="w-3.5 h-3.5 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[300px]" data-testid="org-menu">
              <DropdownMenuLabel className="text-[10px] font-mono uppercase text-muted-foreground">
                Your workspaces
              </DropdownMenuLabel>
              {orgs.map((o) => (
                <DropdownMenuItem
                  key={o.id}
                  className="flex items-center gap-2 cursor-pointer"
                  onSelect={(e) => {
                    e.preventDefault();
                    doSwitchOrg(o.id);
                  }}
                  data-testid={`org-option-${o.slug}`}
                >
                  <div className="w-5 h-5 rounded bg-primary/15 text-primary text-[10px] font-semibold flex items-center justify-center">
                    {o.name.slice(0, 1)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{o.name}</div>
                    <div className="text-[10px] font-mono text-muted-foreground truncate">
                      {o.slug} · {o.role}
                    </div>
                  </div>
                  {o.id === activeOrgId && (
                    <Check className="w-4 h-4 text-primary" />
                  )}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <div className="p-2">
                <form
                  onSubmit={doCreateOrg}
                  className="flex items-center gap-1.5"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Input
                    value={newOrgName}
                    onChange={(e) => setNewOrgName(e.target.value)}
                    placeholder="New workspace name"
                    className="h-8 text-sm"
                    data-testid="new-org-input"
                  />
                  <Button
                    type="submit"
                    size="sm"
                    className="h-8 shrink-0"
                    disabled={creatingOrg || !newOrgName.trim()}
                    data-testid="submit-new-org"
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </Button>
                </form>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
          )}

          {/* Global search — opens the ⌘K palette */}
          <div className="relative flex-1 max-w-md ml-3">
            <button
              type="button"
              onClick={() => setPaletteOpen(true)}
              className="w-full h-9 pl-8 pr-16 flex items-center rounded-md border border-transparent bg-muted/40 text-sm text-muted-foreground hover:border-border hover:bg-muted/60 transition-colors text-left"
              data-testid="global-search-trigger"
            >
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2" />
              <span className="truncate">Search anything…</span>
              <kbd className="ml-auto hidden sm:inline-flex items-center gap-0.5 rounded border border-border bg-white px-1.5 h-5 text-[10px] font-mono">
                ⌘K
              </kbd>
            </button>
          </div>

          <div className="ml-auto flex items-center gap-1">
            <NotificationsBell />

            {/* User menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-9 gap-2 px-2" data-testid="user-menu">
                  <div className="w-7 h-7 rounded-full bg-primary/15 text-primary text-xs font-semibold flex items-center justify-center overflow-hidden">
                    {user?.avatar_url ? (
                      <img
                        src={user.avatar_url}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      (user?.name || user?.email || "?").slice(0, 1).toUpperCase()
                    )}
                  </div>
                  <ChevronDown className="w-3.5 h-3.5 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[240px]">
                <div className="px-2 py-2">
                  <div className="text-sm font-medium truncate">{user?.name}</div>
                  <div className="text-[11px] font-mono text-muted-foreground truncate">
                    {user?.email}
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => nav("/settings/profile")} data-testid="menu-profile">
                  <UserIcon className="w-4 h-4 mr-2" /> Profile
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => nav("/settings/organization")} data-testid="menu-org">
                  <Settings className="w-4 h-4 mr-2" /> Organization
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => {
                    // Dispatch a keydown "?" so GlobalHotkeys opens the dialog.
                    window.dispatchEvent(new KeyboardEvent("keydown", { key: "?" }));
                  }}
                  data-testid="menu-shortcuts"
                >
                  <Keyboard className="w-4 h-4 mr-2" /> Keyboard shortcuts
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => nav("/help")}
                  data-testid="menu-help"
                >
                  <BookOpen className="w-4 h-4 mr-2" /> Help center
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={async (e) => {
                    e.preventDefault();
                    const { startTour } = await import("@/lib/tourManager");
                    const path = location.pathname;
                    let key = "dashboard";
                    if (path.startsWith("/browse")) key = "browse";
                    else if (path.includes("/records") || path.includes("/fields")) key = "collection";
                    startTour(key);
                  }}
                  data-testid="menu-tour"
                >
                  <Sparkles className="w-4 h-4 mr-2" /> Take a tour of this page
                </DropdownMenuItem>
                <InstallAppMenuItem />
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={logout}
                  className="text-destructive focus:text-destructive"
                  data-testid="menu-logout"
                >
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Content */}
        <TabBar />
        <main className="flex-1 min-w-0 overflow-auto" data-testid="app-content">
          <Outlet />
        </main>
      </div>

      {/* Global cmd+K palette */}
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  );
}
